import { Logo, cx } from "@unizpay/ui";
import { ArrowLeftRight, Code2, CreditCard, Home, type LucideIcon, Settings, Wallet } from "lucide-react";
import { NavLink } from "react-router";
import { AccountMenu } from "./AccountMenu";
import { ProfileSwitcher } from "./ProfileSwitcher";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

/**
 * Pages of the active profile: they change when another profile is picked in the switcher. First
 * the day-to-day pages, then the setup: API keys and webhooks, and settings (payouts among them).
 */
const NAV_GROUPS: Array<{ id: string; items: NavItem[] }> = [
  {
    id: "activity",
    items: [
      { to: "/dashboard", label: "Home", icon: Home },
      { to: "/payments", label: "Payments", icon: CreditCard },
      { to: "/transactions", label: "Transactions", icon: ArrowLeftRight },
    ],
  },
  {
    id: "setup",
    items: [
      { to: "/developers", label: "Developers", icon: Code2 },
      { to: "/settings", label: "Settings", icon: Settings },
    ],
  },
];

/** The signed-in person's own pages: the same in every profile. */
const PERSONAL: NavItem[] = [{ to: "/wallet", label: "Wallet", icon: Wallet }];

/**
 * One sidebar link. The current page reads as selected without shouting: a neutral fill, the
 * text in full strength, the icon in the accent colour and a short accent bar on the edge. Hover
 * is a lighter fill, so it never looks like a second selected item.
 */
function NavItemLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cx(
          "group relative flex h-8 items-center gap-3 rounded-md px-2.5 text-[0.8125rem] outline-none",
          "focus-visible:ring-2 focus-visible:ring-accent/40",
          isActive
            ? "bg-subtle font-semibold text-fg"
            : "font-medium text-fg-muted hover:bg-subtle/60 hover:text-fg",
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            className={cx(
              "absolute top-1.5 bottom-1.5 left-0 w-0.5 rounded-full bg-accent",
              !isActive && "invisible",
            )}
          />
          <item.icon
            aria-hidden="true"
            className={cx(
              "size-4 shrink-0",
              isActive ? "text-accent" : "text-fg-subtle group-hover:text-fg-muted",
            )}
          />
          {item.label}
        </>
      )}
    </NavLink>
  );
}

/**
 * Two levels, by position: the profile switcher and that profile's pages at the top; the
 * signed-in person's own pages (wallet) and account menu at the bottom. Rendered in the desktop
 * sidebar and the mobile drawer.
 */
export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="flex h-full flex-col">
      {/* Logo, switcher and links share one left edge. */}
      <div className="px-3 pt-5 pb-3">
        <div className="px-2.5">
          <Logo />
        </div>
        <div className="mt-4">
          <ProfileSwitcher onSwitch={onNavigate} />
        </div>
      </div>
      <nav aria-label="Profile" className="flex-1 overflow-y-auto px-3 py-2">
        {NAV_GROUPS.map((group, i) => (
          <ul key={group.id} className={cx("space-y-px", i > 0 && "mt-6")}>
            {group.items.map((item) => (
              <li key={item.to}>
                <NavItemLink item={item} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        ))}
      </nav>
      <div className="border-t border-line px-3 py-2">
        <nav aria-label="Personal">
          <ul className="mb-1 space-y-px">
            {PERSONAL.map((item) => (
              <li key={item.to}>
                <NavItemLink item={item} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        </nav>
        <AccountMenu onNavigate={onNavigate} />
      </div>
    </div>
  );
}
